import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import type { TemplateTaskConfig } from '@techgitter/contracts';
import type { TicketTypeTemplate } from '../../../types/ticketTypeTemplate';
import { SmartSelect, type SmartSelectOption } from '../../dropdown';
import { PRIMARY_BTN_SX } from '../../../styles/designSystem';

interface CreateTemplateTaskConfigDialogProps {
  open: boolean;
  ticketTemplates: TicketTypeTemplate[];
  existingTicketTypeIds: string[];
  onClose: () => void;
  onCreate: (config: TemplateTaskConfig) => void;
}

export const CreateTemplateTaskConfigDialog: React.FC<CreateTemplateTaskConfigDialogProps> = ({
  open,
  ticketTemplates,
  existingTicketTypeIds,
  onClose,
  onCreate,
}) => {
  const [ticketTypeId, setTicketTypeId] = useState('');

  useEffect(() => {
    if (!open) setTicketTypeId('');
  }, [open]);

  const trimmed = ticketTypeId.trim();
  const isDuplicate = existingTicketTypeIds.includes(trimmed);
  const isValid = trimmed.length > 0 && !isDuplicate;

  // Pick-one from the known ticket-type templates. The option label is the
  // human display name (what the operator sees and filters on); the value is
  // the ticketTypeId the config is keyed by.
  const options: SmartSelectOption[] = ticketTemplates.map((t) => ({
    value: t.ticketTypeId,
    label: t.displayName,
  }));

  const handleCreate = () => {
    if (!isValid) return;
    onCreate({ ticketTypeId: trimmed, sections: [] });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Create Task Template Config</DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ pt: 1 }}>
          {/* Comfortable-tier trigger (40px/14px) — scoped to this dialog only. */}
          <Box sx={{ '& .sm4rt-select__trigger': { height: 40, fontSize: 14 } }}>
            <SmartSelect
              label="Ticket Type"
              placeholder="Select a ticket type…"
              value={trimmed || null}
              options={options}
              onChange={(v) => setTicketTypeId(v ?? '')}
              renderItem={(option) => (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                  <span className="sm4rt-dd__item-code">{option.label}</span>
                  <span className="sm4rt-dd__item-desc">{option.value}</span>
                </span>
              )}
            />
          </Box>
          <Typography
            variant="caption"
            color={isDuplicate ? 'error' : 'text.secondary'}
            sx={trimmed && !isDuplicate ? { fontFamily: 'monospace' } : undefined}
          >
            {isDuplicate
              ? 'A config for this ticket type already exists'
              : trimmed
                ? `ID: ${trimmed}`
                : 'Pick a ticket type template to attach tasks to'}
          </Typography>
          {isDuplicate && (
            <Alert severity="error">A task template config for "{trimmed}" already exists.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#888' }}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" disableElevation disabled={!isValid} sx={PRIMARY_BTN_SX}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
